import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

export interface DashboardConfigUsageItem {
  config_id: string;
  config_key: string | null;
  count: number;
  percentage: number;
}

export interface DashboardMetrics {
  window: {
    days: number | null;
    start_date: string | null;
    end_date: string | null;
    scope: 'all' | 'user';
    target_user_id: string | null;
    model_type: 'all' | 'extraction' | 'classification';
  };
  scan: {
    scanned_results: number;
    limit: number;
    truncated: boolean;
  };
  totals: {
    results: number;
    finalized: number;
  };
  status_counts: {
    pending: number;
    completed: number;
    failed: number;
    cancelled: number;
  };
  unique_interactions: number;
  success_rate: number;
  token_usage: {
    input: number;
    output: number;
    total: number;
    avg_per_result: number;
  };
  execution_time_s: {
    avg_seconds: number;
    p95_seconds: number;
  };
  top_configurations: {
    delfos_server_config_id: DashboardConfigUsageItem[];
    delfos_llm_config_id: DashboardConfigUsageItem[];
    di2winocr_server_config_id: DashboardConfigUsageItem[];
    di2winocr_features_config_id: DashboardConfigUsageItem[];
  };
  top_interactions_by_tokens: Array<{
    rank: number;
    interaction_id: string;
    interaction_name: string;
    user_name: string | null;
    user_email: string | null;
    total_tokens: number;
  }>;
  top_interactions_total: number;
  top_interactions_page: number;
  top_interactions_page_size: number;
  top_interactions_total_pages: number;
  top_model_tokens: Array<{
    model_id: string;
    model_name: string;
    input: number;
    output: number;
    total: number;
  }>;
  file_types: Array<{
    extension: string;
    count: number;
  }>;
}

export interface DashboardMetricsResponse {
  message: string;
  metrics: DashboardMetrics;
}

export interface ModelSeriesPoint {
  date: string;
  model_type: 'extraction' | 'classification';
  count: number;
}

export interface DashboardModelSeries {
  window: {
    days: number | null;
    start_date: string | null;
    end_date: string | null;
    scope: 'all' | 'user';
    target_user_id: string | null;
  };
  points: ModelSeriesPoint[];
}

export interface DashboardModelSeriesResponse {
  message: string;
  series: DashboardModelSeries;
}

@Injectable({
  providedIn: 'root'
})
export class DashboardService {
  constructor(private http: HttpClient) {}

  getMetrics(params: {
    scope: 'all' | 'user';
    targetUserId?: string | null;
    modelType?: 'all' | 'extraction' | 'classification';
    days?: number | null;
    startDate?: string | null;
    endDate?: string | null;
    limit?: number;
    topSearch?: string | null;
    topPage?: number;
    topPageSize?: number;
  }): Observable<DashboardMetricsResponse> {
    let httpParams = new HttpParams().set('scope', params.scope);

    if (params.targetUserId) {
      httpParams = httpParams.set('target_user_id', params.targetUserId);
    }

    if (params.modelType) {
      httpParams = httpParams.set('model_type', params.modelType);
    }

    if (params.days !== undefined && params.days !== null) {
      httpParams = httpParams.set('days', String(params.days));
    }

    if (params.startDate) {
      httpParams = httpParams.set('start_date', params.startDate);
    }

    if (params.endDate) {
      httpParams = httpParams.set('end_date', params.endDate);
    }

    if (params.limit !== undefined && params.limit !== null) {
      httpParams = httpParams.set('limit', String(params.limit));
    }

    if (params.topSearch !== undefined && params.topSearch !== null && params.topSearch.trim() !== '') {
      httpParams = httpParams.set('top_search', params.topSearch.trim());
    }

    if (params.topPage !== undefined && params.topPage !== null) {
      httpParams = httpParams.set('top_page', String(params.topPage));
    }

    if (params.topPageSize !== undefined && params.topPageSize !== null) {
      httpParams = httpParams.set('top_page_size', String(params.topPageSize));
    }

    return this.http.get<DashboardMetricsResponse>('/api/dashboard/metrics', { params: httpParams });
  }

  getModelsSeries(params: {
    scope: 'all' | 'user';
    targetUserId?: string | null;
    days?: number | null;
    startDate?: string | null;
    endDate?: string | null;
  }): Observable<DashboardModelSeriesResponse> {
    let httpParams = new HttpParams().set('scope', params.scope);

    if (params.targetUserId) {
      httpParams = httpParams.set('target_user_id', params.targetUserId);
    }

    if (params.days !== undefined && params.days !== null) {
      httpParams = httpParams.set('days', String(params.days));
    }

    if (params.startDate) {
      httpParams = httpParams.set('start_date', params.startDate);
    }

    if (params.endDate) {
      httpParams = httpParams.set('end_date', params.endDate);
    }

    return this.http.get<DashboardModelSeriesResponse>('/api/dashboard/models-series', { params: httpParams });
  }
}
